import React from "react"
import { Box, Button, Typography } from "@mui/material"
import WarnChip from "@/components/ui/WarnChip"
import { alpha } from "@mui/material/styles"
import { DANGER, DANGER_TINT, PANEL_TEXT_FAINT, WW_BLUE } from "@/theme/tokens"
import { routeErrorChipLabel } from "@/features/voyagePlanner/routeError"

const ERROR_TEXT = "#5f2120"
const CAPTION_SX = {
  fontSize: "11px",
  fontWeight: 700,
  color: PANEL_TEXT_FAINT,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  mb: 0.75,
}
const SUBCARD_SX = { border: "1px solid #e1e8ee", borderRadius: "8px", px: 1.5, py: 1.25 }

function EndpointDot() {
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        flexShrink: 0,
        borderRadius: "50%",
        border: `2px solid ${WW_BLUE}`,
        bgcolor: "#fff",
      }}
    />
  )
}

function BreakMarker() {
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: DANGER,
        color: "#fff",
        fontSize: "12px",
        fontWeight: 700,
        lineHeight: "18px",
        textAlign: "center",
      }}
    >
      ×
    </Box>
  )
}

const DashedLine = () => (
  <Box sx={{ flex: 1, borderTop: "2px dashed #c9a3a3", mx: 0.75 }} />
)

function RouteStrip({ target, departureName, arrivalName }) {
  const portSx = (affected) => ({
    fontSize: "12px",
    color: affected ? ERROR_TEXT : WW_BLUE,
    fontWeight: affected ? 700 : 400,
  })
  return (
    <Box
      sx={{
        bgcolor: DANGER_TINT,
        border: `1px solid ${alpha(DANGER, 0.2)}`,
        borderRadius: "8px",
        p: 1.5,
      }}
    >
      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: ERROR_TEXT, mb: 1.25 }}>
        No route calculated
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", height: 18 }}>
        {target === "departure" ? <BreakMarker /> : <EndpointDot />}
        <DashedLine />
        {!target && (
          <>
            <BreakMarker />
            <DashedLine />
          </>
        )}
        {target === "arrival" ? <BreakMarker /> : <EndpointDot />}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mt: 0.75 }}>
        <Typography sx={portSx(target === "departure")} noWrap>
          {departureName}
        </Typography>
        <Typography sx={{ ...portSx(target === "arrival"), textAlign: "right" }} noWrap>
          {arrivalName}
        </Typography>
      </Box>
    </Box>
  )
}

function AffectedPosition({ target, endpoint, onShowOnMap }) {
  return (
    <Box sx={SUBCARD_SX}>
      <Typography sx={CAPTION_SX}>Affected position</Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            borderRadius: "50%",
            border: `2px solid ${DANGER}`,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: WW_BLUE }} noWrap>
            {target === "arrival" ? "Arrival" : "Departure"} · {endpoint?.name}
          </Typography>
          {endpoint?.coords && (
            <Typography
              sx={{ fontSize: "12px", color: WW_BLUE, fontVariantNumeric: "tabular-nums" }}
            >
              {endpoint.coords}
            </Typography>
          )}
        </Box>
        {onShowOnMap && (
          <Button
            size="small"
            onClick={onShowOnMap}
            sx={{
              flexShrink: 0,
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 700,
              color: WW_BLUE,
            }}
          >
            Show on map
          </Button>
        )}
      </Box>
    </Box>
  )
}

function RequestSummary({ rows }) {
  return (
    <Box sx={SUBCARD_SX}>
      <Typography sx={CAPTION_SX}>Request</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.25 }}>
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <Typography sx={{ fontSize: "12px", color: PANEL_TEXT_FAINT }}>{label}</Typography>
            <Typography sx={{ fontSize: "12px", color: WW_BLUE }}>{value}</Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
}

const formatAttemptTime = (iso) => {
  const d = iso ? new Date(iso) : null
  if (!d || Number.isNaN(d.getTime())) return null
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`
}

// Result step state after a failed route request (replaces the empty state).
export default function ResultErrorState({
  routeError,
  departure,
  arrival,
  departureTime,
  speedLabel,
  onShowOnMap,
}) {
  const target = routeError.kind === "ENDPOINT_UNREACHABLE" ? routeError.target : null
  const transient = routeError.kind === "NETWORK" || routeError.kind === "SERVER"
  const attemptTime = transient ? formatAttemptTime(routeError.attemptedAt) : null
  const departureName = departure?.name ?? "Departure"
  const arrivalName = arrival?.name ?? "Arrival"

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <RouteStrip target={target} departureName={departureName} arrivalName={arrivalName} />
      <Box>
        <WarnChip severity="error">{routeErrorChipLabel(routeError)}</WarnChip>
      </Box>
      <Typography sx={{ fontSize: "13px", lineHeight: 1.45, color: WW_BLUE }}>
        {routeError.message}
      </Typography>
      {target && (
        <AffectedPosition
          target={target}
          endpoint={target === "arrival" ? arrival : departure}
          onShowOnMap={onShowOnMap ? () => onShowOnMap(target) : null}
        />
      )}
      <RequestSummary
        rows={[
          ["Route", `${departureName} → ${arrivalName}`],
          ["Departure", departureTime || "–"],
          ["Speed", speedLabel || "–"],
        ]}
      />
      {attemptTime && (
        <Typography sx={{ fontSize: "11px", color: PANEL_TEXT_FAINT }}>
          Last attempt {attemptTime} UTC · your inputs are kept.
        </Typography>
      )}
    </Box>
  )
}
